const MINUTE_BUCKETS = [5, 10, 15, 30, 45] as const;
const HOUR_BUCKETS_MINUTES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
  (h) => h * 60,
);
const FIXED_BUCKETS_MINUTES = [...MINUTE_BUCKETS, ...HOUR_BUCKETS_MINUTES];
const DAY_MINUTES = 24 * 60;

/**
 * A function to get the poll interval in milliseconds based on the number of minutes until the next webinar.
 * @param minutesUntil - The number of minutes until the next webinar.
 * @returns The poll interval in milliseconds.
 */
export function getWebinarPollIntervalMs(minutesUntil: number): number {
  // >24 hours poll every hour
  if (minutesUntil > 24 * 60) {
    return 60 * 60 * 1000;
  }
  // >1 hour poll every 10 minutes
  if (minutesUntil > 60) {
    return 10 * 60 * 1000;
  }
  // >30 minutes poll every 5 minutes
  if (minutesUntil > 30) {
    return 5 * 60 * 1000;
  }
  // <30 minutes poll every 2 minutes
  return 2 * 60 * 1000;
}

function nearestValue(target: number, candidates: number[]): number {
  let best = candidates[0];
  let bestDist = Math.abs(target - best);
  for (let i = 1; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    const dist = Math.abs(target - candidate);
    // Ties → larger bucket (do not understate urgency).
    if (dist < bestDist || (dist === bestDist && candidate > best)) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

function formatBucketMinutes(bucketMinutes: number): string {
  if (bucketMinutes < 60) {
    return `${bucketMinutes} minutes`;
  }
  if (bucketMinutes < DAY_MINUTES) {
    const hours = bucketMinutes / 60;
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  const days = bucketMinutes / DAY_MINUTES;
  return days === 1 ? "1 day" : `${days} days`;
}

/**
 * Round minutes-until to the nearest display bucket:
 * 5/10/15/30/45 minutes, 1–12 hours, then whole days.
 */
export function formatNextWebinarBucket(minutesUntil: number): string {
  const minutes = Math.max(0, minutesUntil);
  const nearestDays = Math.max(1, Math.round(minutes / DAY_MINUTES));
  const dayBucket = nearestDays * DAY_MINUTES;
  const candidates = [...FIXED_BUCKETS_MINUTES, dayBucket];
  return formatBucketMinutes(nearestValue(minutes, candidates));
}

/**
 * A function to check if the webinar is urgent.
 * @param inProgress - Whether the webinar is in progress.
 * @param minutesUntil - The number of minutes until the next webinar.
 * @returns Whether the webinar is urgent.
 */
export function isWebinarUrgent(
  inProgress: boolean,
  minutesUntil: number | null,
): boolean {
  if (inProgress) {
    return true;
  }
  if (minutesUntil === null) {
    return false;
  }
  return minutesUntil <= 60;
}

export type WebinarStatusLabel = {
  label: string;
  /** Nearest-bucket time, e.g. "30 minutes"; omitted when in progress. */
  time?: string;
};

export function getWebinarStatusLabel(
  inProgress: boolean,
  minutesUntil: number | null,
): WebinarStatusLabel | null {
  if (inProgress) {
    return { label: "Webinar in progress" };
  }
  if (minutesUntil === null) {
    return null;
  }
  return {
    label: "Next webinar in",
    time: formatNextWebinarBucket(minutesUntil),
  };
}
